/**
 * The registrar's decisions over one Classroom intake preview (ADR 0037), and
 * the apply request they make.
 *
 * Nothing is chosen for the registrar except a link IMAM already verified and
 * the proposed primary instructor. A stored choice is read against the
 * current preview, so a re-preview after `preview_changed` keeps every
 * decision that still fits its row and drops the rest without an effect.
 */
import type {
  ClassroomIntakeApplyRequest,
  ClassroomIntakeApplyResult,
  ClassroomIntakePersonRow,
  ClassroomIntakePreview,
} from "@/lib/api/classroomIntake";
import type { PeopleStudentStatus } from "@/lib/api/people";
import { PROTECTED_ROUTES } from "@/navigation/routes";

export interface ChosenPerson {
  person_id: string;
  name: string;
  /** The person's student record; null when the apply issues one. */
  student_id: string | null;
  sid: string | null;
  student_status: PeopleStudentStatus | null;
}

export type RowChoice =
  | { decision: "link_verified" }
  /** A null name part keeps the Classroom name; a string is the registrar's edit. */
  | { decision: "create"; honorific: string; givenName: string | null; familyName: string | null }
  | { decision: "link_person"; person: ChosenPerson | null }
  | { decision: "skip" };

export type Choices = Readonly<Record<string, RowChoice | undefined>>;

export interface SectionDraft {
  mode: "new_draft" | "existing_section";
  code: string;
  /** Null until the registrar edits the prefilled title. */
  title: string | null;
  semesterId: string;
  courseId: string;
}

export interface StudentPlacement {
  programKey: string;
  cohortKey: string;
  /** The chosen cohort's entry year, which issues the student ID's year. */
  cohortYear: number | null;
  admittedYear: string;
}

export const EMPTY_SECTION: SectionDraft = { mode: "new_draft", code: "", title: null, semesterId: "", courseId: "" };
export const EMPTY_PLACEMENT: StudentPlacement = { programKey: "", cohortKey: "", cohortYear: null, admittedYear: "" };

/** The Classroom name of a row, as evidence. */
export function rowName(row: ClassroomIntakePersonRow): string {
  const full = row.name.full?.trim();
  if (full) return full;
  const parts = [row.name.given, row.name.family].map((part) => part?.trim()).filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return row.email ?? "No name in Classroom";
}

/** The name a created person gets: the registrar's edit of each part, else Classroom's. */
export function createdName(
  row: ClassroomIntakePersonRow,
  choice: Extract<RowChoice, { decision: "create" }>,
): { given: string; family: string } {
  return {
    given: (choice.givenName ?? row.name.given ?? "").trim(),
    family: (choice.familyName ?? row.name.family ?? "").trim(),
  };
}

function createdFullName(row: ClassroomIntakePersonRow, choice: Extract<RowChoice, { decision: "create" }>): string {
  const { given, family } = createdName(row, choice);
  return [given, family].filter(Boolean).join(" ") || rowName(row);
}

/** The name edits a create decision sends: only a part the registrar changed. */
function nameEdits(row: ClassroomIntakePersonRow, choice: RowChoice): { given_name?: string; family_name?: string } {
  if (choice.decision !== "create") return {};
  const { given, family } = createdName(row, choice);
  return {
    ...(choice.givenName !== null && given !== (row.name.given ?? "").trim() ? { given_name: given } : {}),
    ...(choice.familyName !== null && family !== (row.name.family ?? "").trim() ? { family_name: family } : {}),
  };
}

/** A created row whose name the registrar cleared to nothing. */
function clearedName(row: ClassroomIntakePersonRow, choice: RowChoice | undefined): boolean {
  if (choice?.decision !== "create") return false;
  const { given, family } = createdName(row, choice);
  return (choice.givenName !== null && !given) || (choice.familyName !== null && !family);
}

/** The decision that stands for a row: its stored choice where it still fits, a verified link by default. */
export function effectiveChoice(row: ClassroomIntakePersonRow, stored: RowChoice | undefined): RowChoice | undefined {
  switch (row.disposition.kind) {
    case "verified":
      return stored?.decision === "skip" ? stored : { decision: "link_verified" };
    case "needs_choice":
      return stored && stored.decision !== "link_verified" ? stored : undefined;
    case "ambiguous":
      return undefined;
  }
}

function decided(choice: RowChoice | undefined): boolean {
  if (!choice) return false;
  return choice.decision !== "link_person" || choice.person !== null;
}

/** A row whose person exists or will be created: what a primary instructor needs. */
function resolvesPerson(choice: RowChoice | undefined): boolean {
  return decided(choice) && choice!.decision !== "skip";
}

/** Teacher rows the registrar may name as primary: not skipped, not ambiguous. */
export function primaryOptions(preview: ClassroomIntakePreview, choices: Choices): ClassroomIntakePersonRow[] {
  return preview.teachers.filter((row) => row.disposition.kind !== "ambiguous"
    && effectiveChoice(row, choices[row.provider_user_id])?.decision !== "skip");
}

/** The primary instructor that stands: the registrar's choice, else the proposed owner, while it is still an option. */
export function effectivePrimary(
  preview: ClassroomIntakePreview,
  choices: Choices,
  chosen: string | null | undefined,
): string | null {
  const candidate = chosen === undefined ? preview.proposed_primary_provider_user_id : chosen;
  if (!candidate) return null;
  return primaryOptions(preview, choices).some((row) => row.provider_user_id === candidate) ? candidate : null;
}

export interface RegisterCounts {
  included: number;
  link: number;
  create: number;
  skip: number;
  undecided: number;
  ambiguous: number;
}

export function registerCounts(rows: readonly ClassroomIntakePersonRow[], choices: Choices): RegisterCounts {
  const counts: RegisterCounts = { included: 0, link: 0, create: 0, skip: 0, undecided: 0, ambiguous: 0 };
  for (const row of rows) {
    if (row.disposition.kind === "ambiguous") {
      counts.ambiguous += 1;
      continue;
    }
    const choice = effectiveChoice(row, choices[row.provider_user_id]);
    if (!decided(choice)) counts.undecided += 1;
    else if (choice!.decision === "skip") counts.skip += 1;
    else if (choice!.decision === "create") counts.create += 1;
    else counts.link += 1;
  }
  counts.included = counts.link + counts.create;
  return counts;
}

/** A student row whose person gets a student record in the apply: a new student, or a linked person who holds none. */
function issuesStudentRecord(row: ClassroomIntakePersonRow, choice: RowChoice | undefined): boolean {
  switch (choice?.decision) {
    case "create":
      return true;
    case "link_verified":
      return row.disposition.kind === "verified" && !row.disposition.student_id;
    case "link_person":
      return !!choice.person && !choice.person.student_id;
    default:
      return false;
  }
}

/** A linked student record that is not active, so the section cannot take it yet. */
export interface StudentRecordHold {
  person_id: string;
  student_id: string;
  sid: string | null;
  status: Exclude<PeopleStudentStatus, "active">;
}

/** The linked student record a row holds back, read from the preview or the chosen person. */
export function studentRecordHold(row: ClassroomIntakePersonRow, choice: RowChoice | undefined): StudentRecordHold | null {
  const record = choice?.decision === "link_verified" && row.disposition.kind === "verified"
    ? row.disposition
    : choice?.decision === "link_person" ? choice.person : null;
  if (!record?.student_id || !record.student_status || record.student_status === "active") return null;
  return { person_id: record.person_id, student_id: record.student_id, sid: record.sid ?? null, status: record.student_status };
}

export function studentRecordHoldLine(hold: StudentRecordHold): string {
  const label = hold.status === "withdrawn" ? "Student record withdrawn" : "Student record inactive";
  return hold.sid ? `${label}, ${hold.sid}` : label;
}


/** How many student rows need the placement that issues a student ID. */
export function issuedStudentRecords(preview: ClassroomIntakePreview, choices: Choices): number {
  return preview.students
    .filter((row) => issuesStudentRecord(row, effectiveChoice(row, choices[row.provider_user_id])))
    .length;
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The one primary action, naming what it will do. */
export function confirmLabel(preview: ClassroomIntakePreview, section: SectionDraft, choices: Choices): string {
  const teachers = registerCounts(preview.teachers, choices).included;
  const students = registerCounts(preview.students, choices).included;
  const target = preview.target.mode === "existing_section" && section.mode === "existing_section"
    ? `Link ${preview.target.code}`
    : "Create draft section";
  return `${target}, ${plural(teachers, "teacher")}, ${plural(students, "student")}`;
}

function sectionTitle(preview: ClassroomIntakePreview, section: SectionDraft): string {
  return section.title ?? preview.proposed_section.title;
}

function admittedYear(placement: StudentPlacement): number | null {
  if (placement.cohortKey) return null;
  const year = Number(placement.admittedYear.trim());
  return Number.isInteger(year) && year >= 1900 && year <= 2999 ? year : null;
}

/** What still stands between the review and its confirmation, in reading order. */
export function blockers(
  preview: ClassroomIntakePreview,
  section: SectionDraft,
  choices: Choices,
  primary: string | null,
  placement: StudentPlacement,
): string[] {
  const found: string[] = [];
  if (section.mode === "new_draft") {
    if (!section.code.trim()) found.push("Type a section code.");
    if (!sectionTitle(preview, section).trim()) found.push("Give the section a title.");
    if (!section.semesterId) found.push("Choose a term.");
  } else if (!section.courseId) {
    found.push("Choose the section to link.");
  } else if (preview.target.mode !== "existing_section" || preview.target.course_id !== section.courseId) {
    found.push("Wait for the review of the chosen section.");
  }
  const teachers = registerCounts(preview.teachers, choices);
  const students = registerCounts(preview.students, choices);
  const ambiguous = teachers.ambiguous + students.ambiguous;
  if (ambiguous > 0) {
    found.push(`${plural(ambiguous, "row matches", "rows match")} more than one person in IMAM. Repair ${ambiguous === 1 ? "it" : "them"} in People, then review again.`);
  }
  const undecided = teachers.undecided + students.undecided;
  if (undecided > 0) found.push(`${plural(undecided, "person still needs", "people still need")} a decision.`);
  if (!primary) {
    found.push("Choose the primary instructor.");
  } else {
    const row = preview.teachers.find((teacher) => teacher.provider_user_id === primary);
    if (row && !resolvesPerson(effectiveChoice(row, choices[primary]))) {
      found.push("Decide the primary instructor's row.");
    }
  }
  const cleared = [...preview.teachers, ...preview.students]
    .filter((row) => clearedName(row, effectiveChoice(row, choices[row.provider_user_id])))
    .map(rowName);
  if (cleared.length > 0) found.push(`Fill in the name you cleared for ${cleared.join(", ")}.`);
  for (const row of preview.students) {
    const choice = effectiveChoice(row, choices[row.provider_user_id]);
    const hold = studentRecordHold(row, choice);
    if (hold) found.push(`${rowName(row)}: ${studentRecordHoldLine(hold)}.`);
  }
  const issued = issuedStudentRecords(preview, choices);
  if (issued > 0 && !placement.cohortKey && admittedYear(placement) === null) {
    found.push(issued === students.create
      ? "New students need a cohort or an admitted year, so their student IDs can be issued."
      : "Students without a student record need a cohort or an admitted year, so their student IDs can be issued.");
  }
  return found;
}

function decision(row: ClassroomIntakePersonRow, choices: Choices) {
  const choice = effectiveChoice(row, choices[row.provider_user_id]);
  if (!decided(choice)) throw new Error(`No decision for ${row.provider_user_id}`);
  return choice!;
}

/** The apply request, exactly per the contract. Call only when `blockers` is empty. */
export function buildApplyRequest(
  preview: ClassroomIntakePreview,
  section: SectionDraft,
  choices: Choices,
  primary: string,
  placement: StudentPlacement,
): ClassroomIntakeApplyRequest {
  const year = admittedYear(placement);
  return {
    provider_course_id: preview.course.provider_course_id,
    preview_digest: preview.preview_digest,
    section: section.mode === "new_draft"
      ? { mode: "new_draft", code: section.code.trim(), title: sectionTitle(preview, section).trim(), semester_id: section.semesterId }
      : { mode: "existing_section", course_id: section.courseId, expected_updated_at: preview.target.mode === "existing_section" ? preview.target.updated_at : undefined },
    primary_provider_user_id: primary,
    teachers: preview.teachers.map((row) => {
      const choice = decision(row, choices);
      return {
        provider_user_id: row.provider_user_id,
        decision: choice.decision,
        ...(choice.decision === "link_person" ? { person_id: choice.person!.person_id } : {}),
        ...(choice.decision === "create" && choice.honorific ? { honorific: choice.honorific } : {}),
        ...nameEdits(row, choice),
      };
    }),
    students: preview.students.map((row) => {
      const choice = decision(row, choices);
      return {
        provider_user_id: row.provider_user_id,
        decision: choice.decision,
        ...(choice.decision === "link_person" ? { person_id: choice.person!.person_id } : {}),
        ...(issuesStudentRecord(row, choice)
          ? {
            ...(placement.programKey ? { program_key: placement.programKey } : {}),
            ...(placement.cohortKey ? { cohort_key: placement.cohortKey } : {}),
            ...(year !== null ? { admitted_year: year } : {}),
          }
          : {}),
        ...nameEdits(row, choice),
      };
    }),
    pair_learners: true,
  };
}

export interface ConfirmedPerson {
  provider_user_id: string;
  name: string;
  register: "teacher" | "student";
  outcome: "linked" | "created" | "skipped";
  /** The IMAM person the row is now, as the apply reported it. */
  person_id: string | null;
  person_name: string | null;
  /** Students: the IMAM student ID. */
  sid: string | null;
}

/** Who the registrar confirmed, and how: the per-person half of the receipt view, with the ids the apply reported. */
export function confirmedPeople(
  preview: ClassroomIntakePreview,
  choices: Choices,
  result: ClassroomIntakeApplyResult,
): ConfirmedPerson[] {
  const read = (register: "teacher" | "student") => (row: ClassroomIntakePersonRow): ConfirmedPerson => {
    const choice = decision(row, choices);
    const applied = result.people.find((entry) => entry.provider_user_id === row.provider_user_id && entry.role === register);
    const verified = row.disposition.kind === "verified" ? row.disposition : null;
    const person_name = choice.decision === "link_verified"
      ? verified?.person_name ?? null
      : choice.decision === "link_person"
        ? choice.person!.name
        : choice.decision === "create"
          ? createdFullName(row, choice)
          : null;
    const skipped = choice.decision === "skip";
    return {
      provider_user_id: row.provider_user_id,
      name: rowName(row),
      register,
      outcome: skipped ? "skipped" : choice.decision === "create" ? "created" : "linked",
      person_id: skipped ? null : applied?.person_id ?? verified?.person_id
        ?? (choice.decision === "link_person" ? choice.person!.person_id : null),
      person_name,
      sid: skipped ? null : applied?.sid ?? verified?.sid ?? null,
    };
  };
  return [...preview.teachers.map(read("teacher")), ...preview.students.map(read("student"))];
}

const DISPOSITION_PHRASE: Record<ClassroomIntakePersonRow["disposition"]["kind"], string> = {
  verified: "is now already in IMAM",
  needs_choice: "now needs a decision",
  ambiguous: "now matches more than one person in IMAM",
};

/** What changed between the preview the registrar reviewed and the current one, in plain sentences. */
export function describePreviewChange(before: ClassroomIntakePreview, after: ClassroomIntakePreview): string[] {
  const changes: string[] = [];
  if (before.course.name !== after.course.name) changes.push(`The course is now named ${after.course.name}.`);
  if (before.proposed_primary_provider_user_id !== after.proposed_primary_provider_user_id) {
    const owner = after.teachers.find((row) => row.provider_user_id === after.proposed_primary_provider_user_id);
    changes.push(owner ? `The Classroom course owner is now ${rowName(owner)}.` : "The Classroom course owner is no longer one of its teachers.");
  }
  for (const [register, label] of [["teachers", "teacher"], ["students", "student"]] as const) {
    const earlier = new Map(before[register].map((row) => [row.provider_user_id, row]));
    const current = new Map(after[register].map((row) => [row.provider_user_id, row]));
    const added = after[register].filter((row) => !earlier.has(row.provider_user_id)).map(rowName);
    const removed = before[register].filter((row) => !current.has(row.provider_user_id)).map(rowName);
    if (added.length > 0) changes.push(`New ${added.length === 1 ? label : `${label}s`} in Classroom: ${added.join(", ")}.`);
    if (removed.length > 0) {
      changes.push(`No longer ${removed.length === 1 ? `a ${label}` : `${label}s`} in Classroom: ${removed.join(", ")}.`);
    }
    for (const row of after[register]) {
      const previous = earlier.get(row.provider_user_id);
      if (previous && previous.disposition.kind !== row.disposition.kind) {
        changes.push(`${rowName(row)} ${DISPOSITION_PHRASE[row.disposition.kind]}.`);
      }
    }
  }
  if (changes.length === 0) changes.push("Classroom changed since this review was read.");
  return changes;
}

const FAILURE_LINES: Readonly<Record<string, string>> = {
  access_denied: "Google Classroom was not connected because access was not allowed. Connect again and allow read-only access.",
  reconnect_required: "Google did not grant the read-only access IMAM needs. Connect again and allow every Classroom permission it asks for.",
  provider_unavailable: "Google Classroom could not be reached, so it was not connected. Try again in a moment.",
  unavailable: "Google Classroom could not be reached, so it was not connected. Try again in a moment.",
  forbidden: "Your IMAM sign-in could not confirm the connection. Sign in again, then connect.",
  unauthenticated: "Your IMAM sign-in could not confirm the connection. Sign in again, then connect.",
  account_in_use: "This Google account is already connected to another IMAM person.",
};

/** The plain sentence for a failed connection's `reason`. */
export function connectionFailureLine(reason: string | null): string {
  return (reason && FAILURE_LINES[reason]) || "Google Classroom was not connected. Connect again with your own Google account.";
}

/** The stored receipt of one confirmed intake, on the intake page. */
export function intakeReceiptPath(receiptId: string): string {
  return `${PROTECTED_ROUTES.admin.classroomIntake}?receipt=${encodeURIComponent(receiptId)}`;
}
